import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@phosphor-icons/react/dist/csr/Plus';

import { PageActions } from '../../app/shell/PageActions';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';

import { CityFormDialog } from './CityFormDialog';
import { ReferenceLifecycleActions } from './ReferenceLifecycleActions';
import { Button } from '../../design-system/primitives/Button/Button';
import {
  ClientDataTable,
  type TableFilter,
} from '../../design-system/composites/DataTable/ClientDataTable';
import {
  type Column,
  type ListStatus,
  type Row,
} from '../../design-system/composites/DataTable/DataTable';
import { ApiError } from '../../lib/api/client';
import { citiesApi, type City } from '../../lib/api/reference';
import styles from './ReferenceList.module.css';

/**
 * The city list — the middle of the reference hierarchy. See
 * `DispensaryList.tsx`'s docblock for the shared reasoning: `include_inactive
 * =true` always, the row-marking `renderStatus` relies on, and the merge-not-
 * refetch discipline on save/change/delete.
 *
 * EACH CITY ONCE, WITH NO DISPENSARY COLUMN. Cities are global since the
 * API's `make_cities_global` migration; this list used to load the
 * dispensary catalogue to label a "Dispensary" column, and showed every city
 * once per dispensary that served it (288 rows for 98 places). What a reader
 * of this list can still usefully ask — "is anything actually in this
 * city?" — is answered by the "Centres" column, `centres_count` from the
 * API: live centres across every dispensary. Which dispensary owns which
 * centre is the centre list's business (its own Dispensary column).
 */

export interface CityListProps {
  permissions: readonly string[];
}

interface CityRow extends Row, City {}

function toRow(node: City): CityRow {
  return { ...node, id: node.uuid };
}

export function CityList({ permissions }: CityListProps) {
  const { t, i18n } = useTranslation();
  const canManage = permissions.includes('reference.manage');

  const [status, setStatus] = useState<ListStatus>('loading');
  const [rows, setRows] = useState<readonly CityRow[]>([]);
  const [reference, setReference] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingNode, setEditingNode] = useState<City | undefined>(undefined);

  const load = useCallback(async () => {
    setStatus('loading');
    setReference(null);

    try {
      const list = await citiesApi.list(true);
      const mapped = list.map(toRow);
      setRows(mapped);
      setStatus(mapped.length === 0 ? 'empty' : 'ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setReference(error.requestId ?? null);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function nodeLabel(node: City): string {
    return i18n.language.startsWith('ar') ? node.name_ar : node.name_en;
  }

  function applyRowChange(uuid: string, changes: Partial<City>): void {
    setRows((current) => current.map((row) => (row.id === uuid ? { ...row, ...changes } : row)));
  }

  function applyRowDelete(uuid: string): void {
    setRows((current) => current.filter((row) => row.id !== uuid));
  }

  function openCreateForm(): void {
    setEditingNode(undefined);
    setFormOpen(true);
  }

  function openEditForm(node: City): void {
    setEditingNode(node);
    setFormOpen(true);
  }

  function closeForm(): void {
    setFormOpen(false);
  }

  function handleSaved(saved: City): void {
    setRows((current) => {
      const exists = current.some((row) => row.id === saved.uuid);
      return exists
        ? current.map((row) => (row.id === saved.uuid ? { ...row, ...saved } : row))
        : [...current, toRow(saved)];
    });
    if (status === 'empty') setStatus('ready');
    setFormOpen(false);
  }

  function renderName(row: Row): ReactNode {
    return nodeLabel(row as unknown as City);
  }

  /**
   * Blank, not `0`, when the count is absent — a row merged in from a
   * create/edit response carries no `centres_count` (only the list endpoint
   * counts), and printing `0` for "not counted" would be a claim this
   * screen cannot back.
   */
  function renderCentres(row: Row): ReactNode {
    const count = (row as unknown as City).centres_count;
    return count === undefined ? null : count;
  }

  function renderStatus(row: Row): ReactNode {
    return (row as unknown as City).is_active
      ? t('reference.statusActive')
      : t('reference.statusInactive');
  }

  function renderActions(row: Row): ReactNode {
    const node = row as unknown as City;
    return (
      <ReferenceLifecycleActions
        node={node}
        label={nodeLabel(node)}
        canManage={canManage}
        onEdit={openEditForm}
        onChanged={applyRowChange}
        onDeleted={applyRowDelete}
        activate={citiesApi.activate}
        deactivate={citiesApi.deactivate}
        remove={citiesApi.remove}
      />
    );
  }

  /**
   * The status facet. `is_active` is a BOOLEAN on the row and the Status
   * column renders it as a word, so neither the search box nor a sort on
   * that column could ever answer "show me the inactive ones" — the word
   * the reader would search for is not on the row at all.
   */
  const filters: readonly TableFilter[] = [
    {
      id: 'status',
      label: t('reference.columnStatus'),
      key: 'is_active',
      options: [
        { value: 'true', label: t('reference.statusActive') },
        { value: 'false', label: t('reference.statusInactive') },
      ],
    },
  ];

  const columns: readonly Column[] = [
    { key: 'name_en', label: t('reference.columnName'), render: renderName },
    { key: 'external_code', label: t('reference.columnCode'), neverMirrors: true },
    { key: 'centres_count', label: t('reference.columnCentreCount'), render: renderCentres },
    { key: 'status', label: t('reference.columnStatus'), render: renderStatus },
    { key: 'actions', label: t('reference.columnActions'), render: renderActions },
  ];

  return (
    <div className={styles['page']}>
      {/* No sub-title under the shell's <h1>; the tab's one command rides in
          the title row beside the level switch. */}
      {canManage ? (
        <PageActions>
          <Button onClick={openCreateForm} icon={<PlusIcon size={16} />}>
            {t('reference.addCity')}
          </Button>
        </PageActions>
      ) : null}

      <ClientDataTable
        caption={t('reference.tabCities')}
        columns={columns}
        rows={rows}
        filters={filters}
        status={status}
        empty={{ body: t('reference.emptyCities') }}
        error={{ title: t('reference.loadFailedCities') }}
      />

      {status === 'error' ? (
        <div className={styles['recovery']}>
          <Button
            variant="secondary"
            onClick={() => {
              void load();
            }}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('reference.retry')}
          </Button>

          {reference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="cityListRequestId">
                {reference}
              </bdi>
            </p>
          )}
        </div>
      ) : null}

      <CityFormDialog
        open={formOpen}
        node={editingNode}
        onClose={closeForm}
        onSaved={handleSaved}
      />
    </div>
  );
}
